import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchStipulationsApi } from "../../../api/client";
import { mapStipulationList } from "./mapStipulations";
import { stipulationKeys } from "./stipulationKeys";
import type { StipulationListView } from "./types";

export interface StipulationListFilters {
  approvalId: string;
  openOnly?: boolean;
}

/**
 * Shared queryOptions for the stipulations list (key + queryFn). Fetches the
 * panel view from the BFF via the generated client and maps it to the panel
 * view model, so the cache stores render-ready data. `openOnly` is a
 * server-side filter and is keyed independently.
 * @param {string} approvalId - The approval whose stipulations to fetch.
 * @param {boolean} [openOnly] - When true, ask the BFF for open stipulations only.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function stipulationsQuery(approvalId: string, openOnly?: boolean) {
  return queryOptions({
    queryKey: stipulationKeys.list({ approvalId, openOnly }),
    queryFn: async ({ signal }): Promise<StipulationListView> => {
      const response = await workbenchStipulationsApi.getStipulations(approvalId, openOnly, undefined, { signal });
      return mapStipulationList(response.data);
    },
    staleTime: 30_000,
    enabled: !!approvalId,
  });
}

/**
 * Reads the stipulations list for an approval, optionally limited to open
 * stipulations by the BFF.
 * @param {StipulationListFilters} filters - Approval and optional open-only flag.
 * @returns {ReturnType<typeof useQuery<StipulationListView>>} The TanStack Query result.
 */
export function useStipulations(filters: StipulationListFilters) {
  return useQuery(stipulationsQuery(filters.approvalId, filters.openOnly));
}
